import { CartAddEvent, CartUpdateEvent } from '@theme/events';

const config = document.querySelector('[data-mdf-sc-config]');
const root = window.Shopify?.routes?.root || '/';
let adding = false;
let recentCart = null;
let disabledControls = [];
const seen = new Set();

function analyticsAllowed() {
  return config?.dataset.analytics === 'true' && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
}

function track(name, detail = {}) {
  if (!analyticsAllowed()) return;
  try {
  const payload = { source: 'mdf_smart_cart', version: '1', ...detail };
  window.Shopify?.analytics?.publish?.(`mdf_smart_cart_${name}`, payload)?.catch?.(() => {});
  window.dataLayer?.push?.({ event: `mdf_smart_cart_${name}`, ...payload });
  window.clarity?.('event', `mdf_smart_cart_${name}`);
  if (typeof detail.cart_value === 'number') window.clarity?.('set', 'mdf_cart_value', String(detail.cart_value));
  if (typeof detail.value_added === 'number') window.clarity?.('set', 'mdf_cart_value_added', String(detail.value_added));
  } catch {}
}

function state() {
  const el = document.querySelector('[data-mdf-cart-state]');
  return { cart_value: Number(el?.dataset.total || 0) / 100, currency: el?.dataset.currency || 'EUR' };
}

function errorFor(form, text) {
  let error = form.querySelector('[data-mdf-cart-error]');
  if (!error) {
    error = document.createElement('p');
    error.dataset.mdfCartError = '';
    error.className = 'mdf-sc-error';
    error.setAttribute('role', 'alert');
    form.append(error);
  }
  error.textContent = text;
}

async function refreshCart() {
  const cart = await readCart();
  const ids = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].slice(0, 5);
  const url = new URL(window.location.href);
  url.searchParams.delete('section_id');
  url.searchParams.set('sections', ids.join(','));
  const response = await fetch(url, { cache: 'no-store' });
  const sections = response.ok ? await response.json() : {};
  document.dispatchEvent(new CartUpdateEvent(cart, 'mdf-smart-cart-refresh', { source: 'mdf-smart-cart-refresh', sections, itemCount: cart.item_count }));
}

function setBusy(active) {
  document.documentElement.classList.toggle('mdf-sc-busy', active);
  if (active) {
    disabledControls = [...document.querySelectorAll('.mdf-sc input[data-cart-line], .mdf-sc [name="checkout"]')].filter(el => !el.disabled);
    disabledControls.forEach(el => { el.disabled = true; });
  } else {
    disabledControls.forEach(el => { if (el.isConnected) el.disabled = false; });
    disabledControls = [];
  }
}

async function changeQuantity(row, quantity) {
  if (adding || !row?.dataset.key) return;
  adding = true;
  setBusy(true);
  const form = row.closest('form');
  const input = row.querySelector('input[data-cart-line]');
  form?.querySelector('[data-mdf-cart-error]')?.remove();
  try {
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].slice(0, 5);
    const response = await fetch(`${root}cart/change.js`, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ id: row.dataset.key, quantity, sections: sectionIds, sections_url: window.location.pathname }) });
    const cart = await response.json();
    if (!response.ok || cart.errors || cart.status) throw new Error(cart.description || cart.errors || config.dataset.error);
    recentCart = cart;
    document.dispatchEvent(new CartUpdateEvent(cart, 'mdf-smart-cart-change', { source: 'mdf-smart-cart-change', sections: cart.sections, itemCount: cart.item_count }));
    return true;
  } catch (error) {
    try { await refreshCart(); } catch {}
    if (input) input.value = input.defaultValue;
    const currentForm = document.querySelector('.mdf-sc .cart-form') || form;
    if (currentForm) errorFor(currentForm, error.message || config.dataset.error);
    return false;
  } finally {
    adding = false;
    setBusy(false);
  }
}

document.addEventListener('click', event => {
  const button = event.target.closest('.mdf-sc .cart-items__remove, .mdf-sc .cart-items__quantity button[name="plus"], .mdf-sc .cart-items__quantity button[name="minus"]');
  if (!button) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (button.disabled || adding) return;
  const row = button.closest('[data-key]');
  const input = row?.querySelector('input[data-cart-line]');
  const current = Number(input?.value || 1);
  const step = Number(input?.step || 1);
  const min = Number(input?.min || 1);
  const quantity = button.classList.contains('cart-items__remove') ? 0 : button.name === 'plus' ? current + step : Math.max(min, current - step);
  changeQuantity(row, quantity);
}, true);

const quantityTimers = new WeakMap();
function commitQuantity(input) {
  clearTimeout(quantityTimers.get(input));
  if (!input.isConnected || adding) return;
  if (!Number.isFinite(input.valueAsNumber) || input.valueAsNumber < 0 || !input.checkValidity()) {
    const message = input.validationMessage || config.dataset.error;
    input.value = input.defaultValue;
    errorFor(input.closest('form'), message);
    return false;
  }
  if (input.value !== input.defaultValue) return changeQuantity(input.closest('[data-key]'), input.valueAsNumber);
}

document.addEventListener('input', event => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches('.mdf-sc input[data-cart-line]')) return;
  clearTimeout(quantityTimers.get(input));
  quantityTimers.set(input, setTimeout(() => commitQuantity(input), 500));
}, true);

document.addEventListener('keydown', event => {
  if (event.key === 'Enter' && event.target.matches?.('.mdf-sc input[data-cart-line]')) {
    event.preventDefault();
    commitQuantity(event.target);
  }
}, true);

for (const eventName of ['change', 'blur']) document.addEventListener(eventName, event => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches('.mdf-sc input[data-cart-line]')) return;
  event.stopImmediatePropagation();
  commitQuantity(input);
}, true);

async function readCart() {
  const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (!response.ok) throw new Error('Cart unavailable');
  recentCart = await response.json();
  return recentCart;
}

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (form.matches('.mdf-sc .cart-form')) {
    const dirty = [...form.querySelectorAll('input[data-cart-line]')].find(input => input.value !== input.defaultValue);
    if (adding || dirty) {
      event.preventDefault();
      if (!adding && dirty) await commitQuantity(dirty);
    }
    return;
  }
  const recommendation = form.hasAttribute('data-mdf-recommendation');
  const customProduct = form.querySelector('[data-add-to-cart]') && form.querySelector('[data-variant-id]');
  if (!recommendation && !customProduct) return;
  if (form.closest('product-form-component')) return;
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer || !config) return;
  event.preventDefault();
  if (adding) return;
  adding = true;
  const button = event.submitter || form.querySelector('[type="submit"]');
  const label = button?.textContent;
  if (button) { button.disabled = true; button.textContent = config.dataset.adding; }
  form.setAttribute('aria-busy', 'true');
  document.documentElement.classList.add('mdf-sc-busy');
  form.querySelector('[data-mdf-cart-error]')?.remove();
  const before = recommendation ? state() : null;
  const body = new FormData(form);
  if (recommendation && analyticsAllowed()) body.set('properties[_mdf_smart_cart]', 'v1');
  const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].slice(0, 5);
  body.set('sections', sectionIds.join(','));
  body.set('sections_url', window.location.pathname);
  setBusy(true);
  if (recommendation) track('recommendation_click', { ...before, variant_id: String(body.get('id')) });
  let added = false;
  try {
    const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || result.message || config.dataset.error);
    added = true;
    let cart;
    try { cart = await readCart(); } catch { cart = {}; }
    document.dispatchEvent(new CartAddEvent(cart, 'mdf-smart-cart', { source: 'mdf-smart-cart', sections: result.sections, itemCount: cart.item_count, variantId: String(body.get('id')) }));
    drawer.open();
    if (recommendation) track('complement_added', { ...before, variant_id: String(body.get('id')), cart_value_before: before.cart_value, cart_value: typeof cart.total_price === 'number' ? cart.total_price / 100 : before.cart_value, value_added: typeof cart.total_price === 'number' ? (cart.total_price / 100) - before.cart_value : 0 });
  } catch (error) {
    try { await refreshCart(); } catch {}
    errorFor(form, added ? config.dataset.addedRefreshError : error.message || config.dataset.error);
    if (added) drawer.open();
  } finally {
    adding = false;
    setBusy(false);
    form.removeAttribute('aria-busy');
    if (button?.isConnected) { button.disabled = false; button.textContent = label; }
  }
}, true);

class MdfCartRecommendations extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.drawer = this.closest('cart-drawer-component');
    this.drawer?.addEventListener('dialog:open', () => this.observeCards(), { signal: this.abort.signal });
    this.addEventListener('change', event => this.changeVariant(event), { signal: this.abort.signal });
    this.addEventListener('click', event => {
      const link = event.target.closest('[data-mdf-product-link]');
      if (link) track('recommendation_click', { ...state(), product_id: link.closest('[data-product-id]')?.dataset.productId, action: 'view' });
    }, { signal: this.abort.signal });
    this.observeCards();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
  }

  observeCards() {
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const card = entry.target;
        const id = card.querySelector('select')?.value;
        if (id && !seen.has(id) && analyticsAllowed()) { seen.add(id); track('recommendation_view', { ...state(), variant_id: id }); }
        this.observer.unobserve(card);
      }
    }, { threshold: 0.5 });
    this.querySelectorAll('[data-product-id]').forEach(card => this.observer.observe(card));
  }

  changeVariant(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.hasAttribute('data-mdf-variant')) return;
    const option = select.selectedOptions[0];
    const card = select.closest('[data-product-id]');
    if (!option || !card) return;
    card.querySelector('[data-mdf-price]').textContent = option.dataset.mdfFormattedPrice;
    card.querySelectorAll('[data-mdf-product-link]').forEach(link => { link.href = option.dataset.url; });
    const image = card.querySelector('img');
    if (image && option.dataset.image) { image.removeAttribute('srcset'); image.src = option.dataset.image; }
  }
}

if (!customElements.get('mdf-cart-recommendations')) customElements.define('mdf-cart-recommendations', MdfCartRecommendations);

document.addEventListener('dialog:open', event => {
  if (event.target.matches?.('cart-drawer-component.mdf-sc')) track('open', state());
}, true);

document.addEventListener('click', async event => {
  const drawer = event.target.closest('cart-drawer-component.mdf-sc');
  if (!drawer || !event.target.closest('[name="checkout"]')) return;
  if (adding) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }
  const dirty = [...drawer.querySelectorAll('input[data-cart-line]')].find(input => input.value !== input.defaultValue);
  if (dirty) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (await commitQuantity(dirty)) document.querySelector('.mdf-sc [name="checkout"]')?.click();
    return;
  }
  track('checkout', state());
}, true);

document.addEventListener('visitorConsentCollected', () => {
  if (document.querySelector('cart-drawer-component.mdf-sc dialog[open]')) {
    track('open', state());
    document.querySelectorAll('mdf-cart-recommendations').forEach(el => el.observeCards?.());
  }
});
